import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuthStore } from '../auth/useAuthStore';
import { usePortalAccess } from './PortalAccessContext';
import { PortalBreadcrumb } from './PortalBreadcrumb';
import { PortalTopbar } from './PortalTopbar';
import { findAnalysisLabel } from './analyses';
import { clientPortalApi } from './api';
import './client-portal.css';
import './dashboard.css';
import './routeTerritorial/route-territorial.css';
import { ProgrammingBridgeState, spliceVisorTemplate } from './spliceVisorTemplate';
import { useAnalysisBridges } from './useAnalysisBridges';

type LoadState = 'loading' | 'error' | 'ready';

/**
 * Si el iframe Blob resulta same-origin con el padre, devuelve ese
 * origen (para usarlo como `targetOrigin`/validación de `event.origin`).
 * Si es opaco o inaccesible, `null` — el canal sigue protegido solo por
 * `event.source` + `channelId`, nunca por origin.
 */
function detectSameOriginIframe(iframeWindow: Window | null | undefined): string | null {
  try {
    const origin: string | undefined = iframeWindow?.location.origin;
    if (origin && origin !== 'null' && origin === window.location.origin) {
      return origin;
    }
    return null;
  } catch {
    return null;
  }
}

function ViewerHeaderBar({ analysisId, label }: { analysisId: string; label: string }): JSX.Element {
  return (
    <div
      style={{
        padding: '0.5rem 1rem',
        background: '#06131f',
        borderBottom: '1px solid rgba(86,215,255,0.18)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', minWidth: 0 }}>
          <Link
            to="/analysis"
            style={{
              color: '#56d7ff',
              fontSize: '0.8rem',
              fontWeight: 700,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            ← Sistema de Transporte
          </Link>
          <span
            style={{
              color: '#fff',
              fontSize: '0.85rem',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>
          <span
            style={{
              color: '#8fa3b8',
              fontSize: '0.7rem',
              border: '1px solid rgba(143,163,184,0.35)',
              borderRadius: '4px',
              padding: '1px 6px',
              whiteSpace: 'nowrap',
            }}
            title="Visor técnico anterior — Programación ahora vive dentro de la pantalla operativa de la línea"
          >
            visor anterior / legacy
          </span>
        </div>
        <PortalBreadcrumb
          segments={[
            { label: 'Inicio', to: '/' },
            { label: 'Sistema de Transporte', to: '/analysis' },
            { label },
          ]}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexShrink: 0 }}>
        <Link
          to={`/analysis/${analysisId}`}
          style={{ color: '#b7c6d6', fontSize: '0.8rem', textDecoration: 'none' }}
        >
          ← Volver a la línea
        </Link>
        <Link
          to="/analysis/system-map"
          style={{ color: '#b7c6d6', fontSize: '0.8rem', textDecoration: 'none' }}
        >
          Mapa del sistema
        </Link>
        <Link
          to={`/analysis/${analysisId}/ficha`}
          target="_blank"
          style={{
            color: '#b7c6d6',
            fontSize: '0.8rem',
            textDecoration: 'none',
            border: '1px solid rgba(86,215,255,0.2)',
            borderRadius: '6px',
            padding: '0.3rem 0.7rem',
          }}
        >
          Imprimir ficha
        </Link>
      </div>
    </div>
  );
}

/**
 * `/analysis/:analysisId` — carga el template del visor aprobado
 * (compartido por todos los análisis del portal) y los datos congelados
 * vía los endpoints protegidos de Central, hace el mismo splice que
 * `splice-final-visor-v3.js` en el navegador, y lo renderiza dentro de
 * un `<iframe>` propio. El visor en sí no se reescribe ni se porta a
 * React — es exactamente el mismo HTML/CSS/JS ya aprobado para Línea 500,
 * ahora parametrizado por `analysisId` en vez de fijo.
 *
 * @remarks
 * El iframe usa `src` con una Blob URL, no `srcDoc`. Diagnosticado en
 * vivo: bajo `srcDoc`, el loop de render de MapLibre (basado en
 * `requestAnimationFrame`) nunca tickeaba dentro del iframe — cero
 * requests de tiles, `map.isStyleLoaded()` nunca pasaba a `true`, por lo
 * tanto `addRouteLayers()` nunca corría. Con una Blob URL como `src` (un
 * documento "navegado" real, no el mecanismo especial `about:srcdoc`),
 * el mismo HTML/JS/datos rinden exactamente igual que el visor standalone
 * — confirmado en el navegador antes de aplicar este cambio.
 *
 * PROGRAMACIÓN persiste vía un puente `postMessage`: el template nunca
 * hace fetch autenticado ni conoce el JWT — solo emite eventos de
 * intención (`save`, `historyRequest`, `historyRevisionRequest`) y
 * recibe de vuelta resultados/metadata. Cada mensaje, en ambos sentidos,
 * lleva el `channelId` generado en este mount — junto con verificar
 * `event.source === iframe.contentWindow`, evita que una instancia vieja
 * del iframe (viva un instante tras un re-mount) participe del canal.
 */
export function AnalysisViewerPage(): JSX.Element {
  const { slug } = usePortalAccess();
  const { analysisId } = useParams<{ analysisId: string }>();
  const token = useAuthStore(state => state.token);
  const [state, setState] = useState<LoadState>('loading');
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const channelIdRef = useRef<string>('');
  const targetOriginRef = useRef<string | null>(null);

  const label = analysisId ? findAnalysisLabel(analysisId) : '';

  useEffect(() => {
    if (!token || !analysisId) return;
    let cancelled = false;
    let objectUrl: string | null = null;

    const channelId = crypto.randomUUID();
    channelIdRef.current = channelId;

    Promise.all([
      clientPortalApi.getAnalysisTemplate(slug, analysisId, token),
      clientPortalApi.getAnalysisBundle(slug, analysisId, token),
      clientPortalApi.getAnalysisAudit(slug, analysisId, token),
      clientPortalApi.getProgramming(slug, analysisId, token),
    ])
      .then(([template, bundle, audit, programming]) => {
        if (cancelled) return;
        const programmingState: ProgrammingBridgeState = programming;
        const html = spliceVisorTemplate(template, bundle, audit, programmingState, channelId, {
          analysisId,
          lineName: label,
        });
        objectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        setIframeUrl(objectUrl);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return (): void => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- label deriva de analysisId, ya en deps
  }, [slug, analysisId, token]);

  useAnalysisBridges({
    slug,
    analysisId: analysisId ?? '',
    token,
    iframeRef,
    channelIdRef,
    targetOriginRef,
  });

  function handleIframeLoad(): void {
    targetOriginRef.current = detectSameOriginIframe(iframeRef.current?.contentWindow);
  }

  if (analysisId && state === 'loading') {
    return <div className="portal-loading">Cargando visor {label}…</div>;
  }

  if (!analysisId || state === 'error' || !iframeUrl) {
    return (
      <div className="portal-denied">
        <h1>No se pudo cargar el visor</h1>
        <Link to="/analysis">Volver a Sistema de Transporte</Link>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column' }}>
      <PortalTopbar />
      <ViewerHeaderBar analysisId={analysisId} label={label} />
      <iframe
        ref={iframeRef}
        title={`Visor ${label}`}
        src={iframeUrl}
        onLoad={handleIframeLoad}
        style={{ flex: 1, border: 'none', width: '100%' }}
      />
    </div>
  );
}
